<script lang="ts" setup>   
    import {onMounted,watch,reactive,computed,ref} from 'vue';
    import {interactionType,getSeverityClass,guidLineType,medTab,medInformationType,printClass,reportSectionBreak} from '../lib/Models/InteractionModel';
    import ModalDialog from "../components/ModalDialog.vue";    
    import DrugInteractions from "../components/DrugInteractions.vue";
    import AllergyInteractions from "../components/AllergyInteractions.vue";
    import DiseaseInteractions from "../components/DiseaseInteractions.vue";
    import RxPatientEducation from "../components/RxPatientEducation.vue";
    import MedGuidelines from "../components/MedGuidelines.vue";
    import MedWebLinks from "../components/MedWebLinks.vue";
    import MedInteractionComments from "../components/MedInteractionComments.vue";
    import MedFDBDisclaimer from "../components/MedFDBDisclaimer.vue";
    import ActionButtonV2 from '../components/ActionButtonV2.vue';
    import {ActionButtonStyle} from '../lib/Types/Types';
    import {ModalSize,ButtonDisplaySize,ButtonDisplayStyle} from "../Enums/UiEnums"; 
    import {popAlert} from "../lib/Notification";
    import {openWindowFromVue} from '../lib/Models/CommonItemsModel'; 
 
    const rxReportField1 = ref(); 
    const formData = reactive <
    {   
        identifier: number,
        inputs: {drugName: string, prescriptionID: number, medicationID: number, medicationLevel: number}, 
        reportData: {patient?: string, disclaimer?: string, allergy?: string, drug?: string, disease?: string, patientEducation?: string, patientGuideline?: string, providerGuideline?: string, webLinks?: string, comments?: string},
        tabs: {base: string,allergy: string, drug: string, disease: string, patientEd: string, patientGuide: string, providerGuide: string, webLinks: string, comments: string}
    }>({     
        identifier : 0,
        inputs: {drugName: "", prescriptionID: 0, medicationID: 0, medicationLevel: 0}, 
        reportData: {patient: "", disclaimer: "", allergy: "", drug: "", disease: "", patientEducation: "", patientGuideline : "", providerGuideline: "", webLinks: "", comments: "" },
        tabs: {base: "", allergy: "", drug: "", disease: "", patientEd: "", patientGuide: "", providerGuide: "", webLinks: "", comments: ""}
    });
    onMounted(() => { 
        formData.identifier = (props.prescriptionID !== 0 ? props.prescriptionID : props.medicationID) ?? 0;
        formData.inputs = {drugName: props.drugName, prescriptionID: props.prescriptionID, medicationID: props.medicationID, medicationLevel: props.medicationLevel}; 
        formData.tabs =
            {
                base : "base" + formData.identifier, 
                allergy: "allergy" + formData.identifier, 
                drug: "drug" + formData.identifier,  
                disease: "disease" + formData.identifier, 
                patientEd: "patientEd" + formData.identifier,  
                patientGuide: "patientGuide" + formData.identifier,  
                providerGuide: "providerGuide" + formData.identifier,  
                webLinks: "webLinks" + formData.identifier,  
                comments: "comments" + formData.identifier
            }; 
    });
    
    watch(() => props.showDialog, (newShow,oldShow) => { 
        if ((newShow) && (newShow !== oldShow)) { 
            SelectFirstTab();
        }
    });

    const props = withDefaults( defineProps<
        {
            showDialog: boolean,
            drugName: string,
            patientID: number,
            prescriptionID?: number,
            medicationID?: number,
            medicationLevel?: number,
            minSevertyLevel? : number,
            allergySeverity?: number,
            drugySeverity?: number,
            diseaseSeverity?: number 
        }>(),
        {
            showDialog: false,
            patientID: 0,
            prescriptionID: 0,
            medicationID: 0,
            medicationLevel: 0,
            minSevertyLevel: 0,
            allergySeverity: -1,
            drugySeverity: -1,
            diseaseSeverity: -1
        }); 
    const emit=defineEmits(["closeInformation"]);  
    const allergyDisplay = computed<boolean>(() => {
        if (props.medicationID === 0 && props.allergySeverity < 0) {
            return false;
        }
        return true;
    }); 
    const drugDisplay = computed<boolean>(() => {
        if (props.medicationID === 0 && props.drugySeverity < 0) {
            return false;
         }
         return true;
    }); 
    const diseaseDisplay = computed<boolean>(() => {
        if (props.medicationID === 0 && props.diseaseSeverity < 0 ) {
            return false;
        }
        return true;
     });  
    
    const reportID = computed<string>(() => "reportID1" + ((props.prescriptionID !== 0 ? props.prescriptionID : props.medicationID) ?? 0));

    async function CloseDialog() : Promise<void> {   
        emit('closeInformation');
    } 
    async function printInteractions() : Promise<void> { 
        switch (getCurrentTab()) {
            case medTab.Allergy:
                printReport(formData.reportData.allergy,2); 
                break;
            case medTab.Drug:
                printReport(formData.reportData.drug,2); 
                break;
            case medTab.Disease:
                printReport(formData.reportData.disease,2);  
                break;
            case medInformationType.PatientEducation: 
                printReport(formData.reportData.patientEducation,2); 
                break;
            case medInformationType.PatientGuidelines:
                printReport(formData.reportData.patientGuideline,1); 
                break;
            case medInformationType.ProviderGuidelines: 
                printReport(formData.reportData.providerGuideline,1);
                break;
            case medInformationType.WebLinks:
                printReport(formData.reportData.webLinks,1);
                break;
            case medInformationType.InteractionComments:
                printReport(formData.reportData.comments,1); 
                break;
        }  
    }
    function printReport(report_data : string | undefined, report_type: number) {
        if (!report_data) {
            popAlert("No informaton is available for printing.","Print");
            return;
        } 
        const report_header = "Drug Information";
        const reportTitle = 
            `<div style="${printClass.reportTitle}">Drug Information for</div>` +
            `<div style="${printClass.reportDrugName}">${formData.inputs.drugName}</div>`;

        if (report_type === 1) {              
            rxReportField1.value.innerHTML = 
                    reportTitle + 
                    reportSectionBreak +
                    formData.reportData.patient + 
                    reportSectionBreak +
                    report_data +
                    reportSectionBreak +
                    formData.reportData.disclaimer;
        }
        else {
            rxReportField1.value.innerHTML = 
                    reportTitle + 
                    reportSectionBreak +
                    formData.reportData.patient + 
                    formData.reportData.disclaimer + 
                    reportSectionBreak +
                    report_data;
        }
        const pdf_url = `/officemd/datapages/pdf_report.asp?txtReportSection=HTML&txtReportField=${rxReportField1.value.id}&PID=${props.patientID}&DIS=${report_header}`;
        openWindowFromVue("PDFWIndow", pdf_url,{ noTimestamp: "Y" });
    } 
    async function SelectFirstTab() : Promise<void> {    
        if (props.allergySeverity > 0) {
            document.getElementById(formData.tabs.allergy + "-tab")?.click();
            return;
        }      
        if (props.drugySeverity > 0) { 
            document.getElementById(formData.tabs.drug + "-tab")?.click();
            return;
        }    
        if (props.diseaseSeverity > 0) {
            document.getElementById(formData.tabs.disease + "-tab")?.click();
            return;
        }    
        document.getElementById(formData.tabs.patientEd + "-tab")?.click();
    } 
    function updateModalDisclaimer(success: boolean, patientData:  string, disclaimer: string) :  void { 
        if (success) {
            formData.reportData.patient = patientData; 
            formData.reportData.disclaimer = disclaimer;
        } 
    }
    function updateModalDrug(success: boolean, drugData: string) :  void { 
        if (success) { 
            formData.reportData.drug = drugData;
        } 
    }
    function updateModalDisease(success: boolean, diseaseData: string) :  void { 
        if (success) { 
            formData.reportData.disease = diseaseData;
        } 
    }
    function updateModalAllergy(success: boolean, allergyData: string) :  void { 
        if (success) { 
            formData.reportData.allergy = allergyData;
        } 
    }
    function updateModalPatientEducation(success: boolean, educationData: string) :  void { 
        if (success) { 
            formData.reportData.patientEducation = educationData;
        } 
    }
    function updateModalGuidline(success: boolean,type: guidLineType, guidelineData: string) :  void { 
        if (success) {  
            if (type === guidLineType.Patient) {
                formData.reportData.patientGuideline = guidelineData;
            } 
            else {
                formData.reportData.providerGuideline = guidelineData;
            } 
        } 
    }
    function updateModalWeblink(success: boolean, weblinkData: string) :  void { 
        if (success) { 
            formData.reportData.webLinks = weblinkData;
        } 
    }
    function updateModalComment(success: boolean, commentData: string) :  void { 
        if (success) { 
            formData.reportData.comments = commentData;
        } 
    }
    function getCurrentTab() : medTab | medInformationType | string {
        if (document.getElementById(formData.tabs.allergy + '-tab')?.className.indexOf("active") != -1) {
            return medTab.Allergy;
        };
        if (document.getElementById(formData.tabs.drug + '-tab')?.className.indexOf("active") != -1) {
            return medTab.Drug;
        };
        if (document.getElementById(formData.tabs.disease + '-tab')?.className.indexOf("active") != -1) {
            return medTab.Disease;
        };
        if (document.getElementById(formData.tabs.patientEd + '-tab')?.className.indexOf("active") != -1) {
            return medInformationType.PatientEducation;
        };
        if (document.getElementById(formData.tabs.patientGuide + '-tab')?.className.indexOf("active") != -1) {
            return medInformationType.PatientGuidelines;
        };
        if (document.getElementById(formData.tabs.providerGuide + '-tab')?.className.indexOf("active") != -1) {
            return medInformationType.ProviderGuidelines;
        };
        if (document.getElementById(formData.tabs.webLinks + '-tab')?.className.indexOf("active") != -1) {
            return medInformationType.WebLinks;
        };
        if (document.getElementById(formData.tabs.comments + '-tab')?.className.indexOf("active") != -1) {
            return medInformationType.InteractionComments;
        }; 
        return "";
    }
</script>

<template>
    <ModalDialog :size=ModalSize.Large :show="props.showDialog" :closeOnClickOutside="false" @hidden="CloseDialog">
        <template v-slot:title>
            <div class="fw-bold fs-6 text-isalus-primary pe-1">Prescribing Information for:</div>
            <em  v-show="props.minSevertyLevel ?? 0 > 0" :class="getSeverityClass(props.minSevertyLevel)" :title="`Interaction Level ${props.minSevertyLevel}`"></em>
            <span class="fw-bold fs-6  text-secondary ps-2"> {{formData.inputs.drugName}}</span>
        </template>
        <template #footer>
            <div class="row container-fluid">
                <div class="col text-start">
                    <MedFDBDisclaimer 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        @updateModalDisclaimer="updateModalDisclaimer">
                    </MedFDBDisclaimer>
                </div>
            </div>
            <div class="row container-fluid">
                <div class="col text-end">
                    <span class="pe-2">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Print"  
                            :text="'Print'"  
                            :tooltip="'Print Interactions'"  
                            :size="ButtonDisplaySize.Small" 
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                            :performAction="printInteractions" 
                            :disabled="false" />
                    </span>
                    <span class="pe-2">
                        <ActionButtonV2 
                            :action="ActionButtonStyle.Close"  
                            text="Close"  
                            tooltip="Close Interactions"  
                            :size="ButtonDisplaySize.Small" 
                            :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                            :performAction="CloseDialog" 
                            :disabled="false" />
                    </span>
                </div>
            </div>  
        </template>
        <div >
            <div class="row pt-2"> 
                <ul class="col nav nav-tabs" id="myTab" role="tablist">
                    <li v-show="false" class="nav-item" role="presentation">
                        <button  class="nav-link show active" :id="formData.tabs.base + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.base" type="button" role="tab" :aria-controls="formData.tabs.base" aria-selected="true">base</button>
                    </li> 
                    <li v-show="allergyDisplay" class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.allergy + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.allergy" type="button" role="tab" :aria-controls="formData.tabs.allergy" aria-selected="false">Allergy<br/>Interactions</button>
                    </li>
                    <li v-show="drugDisplay" class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.drug + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.drug" type="button" role="tab" :aria-controls="formData.tabs.drug" aria-selected="false">Drug<br/>Interactions</button>
                    </li>
                    <li v-show="diseaseDisplay" class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.disease + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.disease" type="button" role="tab" :aria-controls="formData.tabs.disease"  aria-selected="false">Disease<br/>Interactions</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.patientEd + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.patientEd" type="button" role="tab" :aria-controls="formData.tabs.patientEd"  aria-selected="false">Patient<br/>Education</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.patientGuide + '-tab'"  data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.patientGuide" type="button" role="tab" :aria-controls="formData.tabs.patientGuide"  aria-selected="false">Patient<br/>Guidelines</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.providerGuide + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.providerGuide" type="button" role="tab" :aria-controls="formData.tabs.providerGuide" aria-selected="false">Provider<br/>Guidelines</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.webLinks + '-tab'"  data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.webLinks" type="button" role="tab" :aria-controls="formData.tabs.webLinks" aria-selected="false">Web<br/>Links</button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link" :id="formData.tabs.comments + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + formData.tabs.comments" type="button" role="tab" :aria-controls="formData.tabs.comments" aria-selected="false">Interaction<br/>Comments</button>
                    </li>
                </ul>
            </div>
            <div class="row pt-2 tab-content" id="myTabContent">
                <div class="col tab-pane fade show active" :id="formData.tabs.base" role="tabpanel" :aria-labelledby="formData.tabs.base + '-tab'">Select an area to view...</div>
                <div v-if="allergyDisplay" class="col tab-pane fade" :id="formData.tabs.allergy" role="tabpanel" :aria-labelledby="formData.tabs.allergy + '-tab'">
                    <AllergyInteractions 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        :medicationLevel="props.medicationLevel"
                        :interactionType="interactionType.Allergy"
                        @updateModalAllergy="updateModalAllergy">
                    </AllergyInteractions>
                </div>
                <div v-if="drugDisplay" class="col tab-pane fade" :id="formData.tabs.drug" role="tabpanel" :aria-labelledby="formData.tabs.drug + '-tab'">
                    <DrugInteractions 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        :medicationLevel="props.medicationLevel"
                        :interactionType="interactionType.Drug"
                        @updateModalDrug="updateModalDrug">
                    </DrugInteractions>
                </div>
                <div v-if="diseaseDisplay" class="col tab-pane fade" :id="formData.tabs.disease" role="tabpanel" :aria-labelledby="formData.tabs.disease + '-tab'">
                    <DiseaseInteractions 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        :medicationLevel="props.medicationLevel"
                        :interactionType="interactionType.Disease"
                        @updateModalDisease="updateModalDisease">
                    </DiseaseInteractions>
                </div>
                <div class="col tab-pane fade" :id="formData.tabs.patientEd" role="tabpanel" :aria-labelledby="formData.tabs.patientEd + '-tab'">
                    <RxPatientEducation 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        :drugName="props.drugName"
                        @updateModalPatientEducation="updateModalPatientEducation">
                    </RxPatientEducation>
                </div>
                <div class="col tab-pane fade" :id="formData.tabs.patientGuide" role="tabpanel" :aria-labelledby="formData.tabs.patientGuide + '-tab'">
                    <MedGuidelines 
                        :showDialog="props.showDialog" 
                        :type = "guidLineType.Patient"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID" 
                        @updateModalGuidline="updateModalGuidline">
                    </MedGuidelines>
                </div>
                <div class="col tab-pane fade" :id="formData.tabs.providerGuide" role="tabpanel" :aria-labelledby="formData.tabs.providerGuide + '-tab'">
                    <MedGuidelines 
                        :showDialog="props.showDialog" 
                        :type = "guidLineType.Provider"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        @updateModalGuidline="updateModalGuidline">
                    </MedGuidelines>
                </div>
                <div class="col tab-pane fade" :id="formData.tabs.webLinks" role="tabpanel" :aria-labelledby="formData.tabs.webLinks + '-tab'">
                    <MedWebLinks 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        @updateModalWeblink="updateModalWeblink">
                    </MedWebLinks>
                </div>
                <div class="col tab-pane fade" :id="formData.tabs.comments" role="tabpanel" :aria-labelledby="formData.tabs.comments + '-tab'">
                    <MedInteractionComments 
                        :showDialog="props.showDialog"
                        :patientID="props.patientID"    
                        :prescriptionID="props.prescriptionID"
                        :medicationID="props.medicationID"
                        @updateModalComment="updateModalComment">
                    </MedInteractionComments>
                </div>
            </div> 
        </div>  
        <div ref="rxReportField1" :id="reportID" style="display:none"></div>
    </ModalDialog>
</template>